import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  HiHandThumbUp,
  HiHandThumbDown,
  HiChatBubbleLeft,
  HiPaperAirplane,
  HiPencil,
  HiTrash,
} from 'react-icons/hi2';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const InterpretationsSection = ({
  onFetchInterpretationComments,
  interpretations,
  interpretationComments,
  onAddInterpretation,
  onAddInterpretationComment,
  onLikeInterpretation,
  onDislikeInterpretation,
  interpretationRefs,
  onViewTag,
  onRefresh,
  user,
}) => {
  const { isOwner, isModerator, canModifyComment, getAuthenticatedApi } = useAuth();
  const [newInterpretation, setNewInterpretation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [visibleCommentReplies, setVisibleCommentReplies] = useState({});
  const [visibleInterpretationComments, setVisibleInterpretationComments] = useState({});

  // Edit states for interpretations
  const [editingInterpretation, setEditingInterpretation] = useState(null);
  const [editInterpretationContent, setEditInterpretationContent] = useState('');
  const [editInterpretationTitle, setEditInterpretationTitle] = useState('');

  // Edit states for comments
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');

  // Check if user can modify interpretation
  const canModifyInterpretation = (interpretation) => {
    if (!user) return false;
    const interpretationUserId = interpretation.user_id || interpretation.user?.id;
    const currentUserId = user?.id || user?.user_id || user?.userId || user?.pk;
    const isInterpretationOwner = currentUserId && interpretationUserId &&
      (String(currentUserId) === String(interpretationUserId) || currentUserId === interpretationUserId);
    return isInterpretationOwner || isModerator();
  };

  // Delete interpretation
  const handleDeleteInterpretation = async (interpretationId) => {
    if (!window.confirm('Delete this interpretation? This will also delete all comments on it.')) return;

    try {
      const api = await getAuthenticatedApi();
      await api.delete(`/interpretations/${interpretationId}`);
      toast.success('Interpretation deleted successfully');
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error deleting interpretation:', error);
      toast.error('Failed to delete interpretation');
    }
  };

  // Update interpretation (both title and text)
  const handleUpdateInterpretation = async (interpretationId) => {
    if (!editInterpretationContent.trim()) return;

    try {
      const api = await getAuthenticatedApi();
      const updateData = {
        text: editInterpretationContent.trim()
      };

      // Include title in update if it exists and has been modified
      if (editInterpretationTitle.trim()) {
        updateData.title = editInterpretationTitle.trim();
      }

      await api.put(`/interpretations/${interpretationId}`, updateData);
      toast.success('Interpretation updated successfully');
      setEditingInterpretation(null);
      setEditInterpretationContent('');
      setEditInterpretationTitle('');
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error updating interpretation:', error);
      toast.error('Failed to update interpretation');
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const api = await getAuthenticatedApi();
      await api.delete(`/comments/${commentId}`);
      toast.success('Comment deleted successfully');
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Update comment
  const handleUpdateComment = async (commentId) => {
    if (!editCommentContent.trim()) return;

    try {
      const api = await getAuthenticatedApi();
      await api.put(`/comments/${commentId}`, {
        content: editCommentContent.trim()
      });
      toast.success('Comment updated successfully');
      setEditingComment(null);
      setEditCommentContent('');
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  // Start editing interpretation
  const startEditInterpretation = (interpretation) => {
    setEditingInterpretation(interpretation.id);
    setEditInterpretationContent(interpretation.text);
    setEditInterpretationTitle(interpretation.shape_data?.title || '');
  };

  // Cancel editing interpretation
  const cancelEditInterpretation = () => {
    setEditingInterpretation(null);
    setEditInterpretationContent('');
    setEditInterpretationTitle('');
  };

  // Start editing comment
  const startEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditCommentContent(comment.content);
  };

  // Cancel editing comment
  const cancelEditComment = () => {
    setEditingComment(null);
    setEditCommentContent('');
  };

  const handleSubmitInterpretation = async (e) => {
    e.preventDefault();
    if (!newInterpretation.trim() || submitting) return;
    setSubmitting(true);
    await onAddInterpretation(newInterpretation.trim());
    if (onRefresh) await onRefresh();
    setNewInterpretation('');
    setSubmitting(false);
  };

  const handleSubmitReply = async (e, interpretationId, parentId = null) => {
    e.preventDefault();
    if (!replyContent.trim() || submitting) return;
    setSubmitting(true);
    await onAddInterpretationComment(replyContent.trim(), parentId, interpretationId);
    if (onRefresh) await onRefresh();
    setReplyContent('');
    setReplyingTo(null);
    setSubmitting(false);
  };

  const toggleCommentReplies = (commentId) => {
    setVisibleCommentReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  // FIXED: This function now fetches comments when toggling visibility
  const toggleInterpretationComments = async (interpId) => {
    const isCurrentlyVisible = visibleInterpretationComments[interpId];

    // Toggle visibility state
    setVisibleInterpretationComments((prev) => ({
      ...prev,
      [interpId]: !prev[interpId],
    }));

    // If we're showing comments and they haven't been fetched yet, fetch them
    if (!isCurrentlyVisible && onFetchInterpretationComments) {
      try {
        await onFetchInterpretationComments(interpId);
      } catch (error) {
        console.error('Error fetching interpretation comments:', error);
        toast.error('Failed to load comments');
        // Revert visibility state on error
        setVisibleInterpretationComments((prev) => ({
          ...prev,
          [interpId]: false,
        }));
      }
    }
  };

  const renderComment = (comment, interpretationId, depth = 0) => {
    // Check ownership for comments
    const commentUserId = comment.user_id || comment.user?.id;
    const currentUserId = user?.id || user?.user_id || user?.userId || user?.pk;
    const isCommentOwner = currentUserId && commentUserId &&
      (String(currentUserId) === String(commentUserId) || currentUserId === commentUserId);
    const canModify = canModifyComment(comment);

    return (
      <motion.div
        key={comment.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`border-l-[2px] border-gray-700 pl-4 ${depth > 0 ? 'ml-4' : ''}`}
      >
        <div className="bg-dark-gray/40 rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-sm font-bold">
              {(comment.user?.name || 'A')[0].toUpperCase()}
            </div>
            <span className="font-medium">{comment.user?.name || 'Anonymous'}</span>
            <span className="text-gray-400 text-sm">
              {new Date(comment.created_at).toLocaleDateString()}
            </span>
          </div>

          {editingComment === comment.id ? (
            // Edit mode for comment
            <div className="mb-3">
              <textarea
                value={editCommentContent}
                onChange={(e) => setEditCommentContent(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleUpdateComment(comment.id)}
                  disabled={!editCommentContent.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={cancelEditComment}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // Display mode for comment
            <p className="text-gray-300 mb-3">{comment.content}</p>
          )}

          {comment.nesting_level < 5 && editingComment !== comment.id && (
            <div className="flex gap-4 items-center text-sm text-gray-400 mt-1">
              <button
                onClick={() => setReplyingTo(comment.id)}
                className="hover:text-blue-400 transition flex items-center gap-1"
              >
                <HiChatBubbleLeft className="w-4 h-4" />
                Reply
              </button>

              {/* Edit button - only for comment owner */}
              {isCommentOwner && (
                <button
                  onClick={() => startEditComment(comment)}
                  className="hover:text-yellow-400 transition flex items-center gap-1"
                >
                  <HiPencil className="w-4 h-4" /> Edit
                </button>
              )}

              {/* Delete button - for comment owner OR moderator */}
              {canModify && (
                <button
                  onClick={() => handleDeleteComment(comment.id)}
                  className="hover:text-red-400 transition flex items-center gap-1"
                >
                  <HiTrash className="w-4 h-4" /> Delete
                </button>
              )}

              {comment.replies?.length > 0 && (
                <button
                  onClick={() => toggleCommentReplies(comment.id)}
                  className="hover:text-blue-400 transition flex items-center gap-1"
                >
                  {visibleCommentReplies[comment.id]
                    ? 'Hide Replies'
                    : `View Replies (${comment.replies.length})`}
                </button>
              )}
            </div>
          )}

          {replyingTo === comment.id && user && (
            <form
              onSubmit={(e) => handleSubmitReply(e, interpretationId, comment.id)}
              className="mt-4"
            >
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                rows={3}
                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="submit"
                  disabled={!replyContent.trim() || submitting}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  <HiPaperAirplane className="inline-block w-4 h-4 mr-1" />
                  Reply
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {comment.replies?.length > 0 && visibleCommentReplies[comment.id] && (
            <div className="mt-2">
              {comment.replies.map((child) =>
                renderComment(child, interpretationId, depth + 1)
              )}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderInterpretation = (interp) => {
    const entry = interpretationComments.find((c) => c.interpretationId === interp.id);
    const comments = entry?.comments || [];
    const roots = comments.filter((c) => c.parent_id === null);
    const canModify = canModifyInterpretation(interp);

    return (
      <motion.div
        ref={(el) => { if (el) interpretationRefs.current[interp.id] = el; }}
        key={interp.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-dark-gray/50 rounded-xl p-6 mb-8 transition-all duration-300"
      >

        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            {editingInterpretation === interp.id ? (
              // Edit mode for title
              <input
                type="text"
                value={editInterpretationTitle}
                onChange={(e) => setEditInterpretationTitle(e.target.value)}
                className="text-l font-semibold text-white bg-gray-700 border border-gray-600 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-primary flex-1 mr-4"
                placeholder="Interpretation title"
              />
            ) : (
              <h3 className="text-l font-semibold text-white break-words">
                {interp.shape_data?.title || 'Interpretation'}
              </h3>
            )}

            {canModify && editingInterpretation !== interp.id && (
              <div className="flex gap-2 text-gray-400">
                <button
                  onClick={() => startEditInterpretation(interp)}
                  className="hover:text-yellow-400 transition flex items-center gap-1"
                >
                  <HiPencil className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => handleDeleteInterpretation(interp.id)}
                  className="hover:text-red-400 transition flex items-center gap-1"
                >
                  <HiTrash className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">{interp.shape_type}</p>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-xs font-bold">
              {(interp.user?.name || 'A')[0].toUpperCase()}
            </div>
            <span className="text-sm text-gray-300">{interp.user?.name || 'Anonymous'}</span>
          </div>
        </div>

        {editingInterpretation === interp.id ? (
          // Edit mode for interpretation
          <div className="mb-4">
            <textarea
              value={editInterpretationContent}
              onChange={(e) => setEditInterpretationContent(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              rows={4}
              placeholder="Interpretation content"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleUpdateInterpretation(interp.id)}
                disabled={!editInterpretationContent.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                Save Changes
              </button>
              <button
                onClick={cancelEditInterpretation}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // Display mode for interpretation
          <p className="text-gray-300 mb-4">{interp.text}</p>
        )}

        <div className="flex gap-4 text-sm text-gray-400 mb-4">
          <button
            onClick={() => onLikeInterpretation(interp.id)}
            className="flex items-center gap-1"
          >
            <HiHandThumbUp className="w-4 h-4" /> {interp.like_count}
          </button>
          <button
            onClick={() => onDislikeInterpretation(interp.id)}
            className="flex items-center gap-1"
          >
            <HiHandThumbDown className="w-4 h-4" /> {interp.dislike_count}
          </button>
          <span className="flex items-center gap-1">
            <HiChatBubbleLeft className="w-4 h-4" /> {roots.length}
          </span>
        </div>

        {user && editingInterpretation !== interp.id && (
          <div className="flex gap-4 items-center text-sm text-gray-400 mb-2">
            <button
              onClick={() =>
                setReplyingTo(replyingTo === `interp-${interp.id}` ? null : `interp-${interp.id}`)
              }
              className="hover:text-blue-400 transition flex items-center gap-1"
            >
              <HiChatBubbleLeft className="w-4 h-4" />
              Reply to Interpretation
            </button>
            <button
              onClick={() => toggleInterpretationComments(interp.id)}
              className="hover:text-blue-400 transition flex items-center gap-1"
            >
              {visibleInterpretationComments[interp.id]
                ? (roots.length > 0 ? 'Hide Comments' : 'Hide Comments')
                : `View Comments${roots.length > 0 ? ` (${roots.length})` : ''}`}
            </button>

            <button
              onClick={() => onViewTag(interp.id)}
              className="hover:text-green-400 transition flex items-center gap-1"
            >
              View Tag on Image
            </button>


          </div>
        )}

        {replyingTo === `interp-${interp.id}` && (
          <form
            onSubmit={(e) => handleSubmitReply(e, interp.id, null)}
            className="mt-2 mb-4"
          >
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write a comment on this interpretation..."
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-2 mt-2">
              <button
                type="submit"
                disabled={!replyContent.trim() || submitting}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                <HiPaperAirplane className="inline-block w-4 h-4 mr-1" /> Comment
              </button>
              <button
                type="button"
                onClick={() => {
                  setReplyingTo(null);
                  setReplyContent('');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {visibleInterpretationComments[interp.id] && (
          <div className="mt-2">
            {roots.map((comment) => renderComment(comment, interp.id))}
          </div>
        )}

        {visibleInterpretationComments[interp.id] && roots.length === 0 && (
          <p className="text-sm text-gray-400 mt-2">No comments yet.</p>
        )}

      </motion.div>
    );
  };

  return (
    <div>
      {interpretations.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No interpretations yet. Be the first!
        </div>
      ) : (
        interpretations.map(renderInterpretation)
      )}
    </div>
  );
};

export default InterpretationsSection;